import { useCallback } from 'react';

import { useSelector } from 'react-redux';

import { StreamTypes } from 'store/player/actions.types';
import { RBProgram } from 'store/railsBuilder/reducer.types';
import { selectCachedRecordingByEventId } from 'store/recordings/selectors';
import {
    selectAppSessionId,
    selectVideoSessionId,
} from 'store/session/selectors';

import { VideoItem, VodEditorial } from 'services/data-manager/types';
import {
    PlaybackStartUavParams,
    UavContentTypes,
    UavEvents,
} from 'services/uav/types';
import useGetBaseUavParams from 'services/uav/useGetBaseUavParams';
import {
    MOBILE_PLAYBACK_CONTENT_SOURCE,
    getPlayBackType,
    getTechnicalId,
} from 'services/uav/utils';

type GetPlaybackStartUavParamsProps = Omit<
    PlaybackStartUavParams['metadata'],
    | 'appSessionId'
    | 'sessionId'
    | 'contentType'
    | 'contentSource'
    | 'editorialID'
    | 'playbackSessionId'
    | 'deviceType'
    | 'programmeId'
    | 'technicalId'
    | 'seriesId'
    | 'appName'
    | 'appVersion'
>;

type Props = {
    playbackType: StreamTypes | undefined;
    videoItem: VideoItem | undefined;
};

const useGetPlaybackStartUavParams = ({ playbackType, videoItem }: Props) => {
    const appSessionId = useSelector(selectAppSessionId);
    const genericVideoSessionId = useSelector(selectVideoSessionId);

    const recordingItem = useSelector(
        selectCachedRecordingByEventId(videoItem?.id),
    );

    const getBaseUavParams = useGetBaseUavParams();

    const getPlaybackStartUavParams = useCallback(
        (
            params: GetPlaybackStartUavParamsProps,
        ): PlaybackStartUavParams | undefined => {
            const baseUavParams = getBaseUavParams(UavEvents.PLAYBACK_START);
            const seriesId = videoItem?.seriesId && {
                seriesId: videoItem.seriesId,
            };

            if (
                baseUavParams &&
                videoItem &&
                appSessionId &&
                genericVideoSessionId
            ) {
                const contentType = getPlayBackType(
                    playbackType,
                    videoItem.originalProgram as VodEditorial | RBProgram,
                );
                const recordingId =
                    (contentType === UavContentTypes.NPVR && {
                        recordingId: recordingItem?.id,
                    }) ||
                    undefined;

                return {
                    ...baseUavParams,
                    metadata: {
                        ...params,
                        appSessionId,
                        contentSource: MOBILE_PLAYBACK_CONTENT_SOURCE,
                        contentType,
                        editorialID: videoItem.id,
                        playbackSessionId: genericVideoSessionId,
                        technicalId:
                            getTechnicalId(
                                videoItem.originalProgram as
                                    | VodEditorial
                                    | RBProgram,
                            ) ?? videoItem.id,
                        programmeId:
                            videoItem.originalProgram?.id ?? videoItem.id,
                        ...recordingId,
                        ...seriesId,
                        ...baseUavParams.metadata,
                    },
                };
            }

            return undefined;
        },
        [
            getBaseUavParams,
            videoItem,
            appSessionId,
            playbackType,
            genericVideoSessionId,
            recordingItem,
        ],
    );

    return {
        getPlaybackStartUavParams,
    };
};

export default useGetPlaybackStartUavParams;
